<script setup lang="ts">
import type { Components } from '@/api/generated/api';
import ExternalMaterialIcon from '@/assets/icons/ExternalMaterialIcon.vue';
import PlusIcon from '@/assets/icons/PlusIcon.vue';
import {
  createCustomExternalMaterial,
  fetchExternalMaterialRecommendations,
} from '@/api/resources/externalMaterial.api';
import { useQueryWithToast } from '@/composables/useQueryWithToast';
import { useMobile } from '@/composables/useMobile';
import { useToast } from '@/composables/useToast';
import { SearchHighlightTextInjectionKey } from '@/composables/useSearchSearchHighlightText';
import PositionablePopover from '@/core/dialogs/PositionablePopover.vue';
import ClickableListItem from '@/core/elements/ClickableListItem.vue';
import EmptyPlaceholder from '@/core/elements/EmptyPlaceholder.vue';
import SelectionPopoverSearchHeader from '@/core/selects/SelectionPopoverSearchHeader.vue';
import { focusSelectionSearchOnOpen } from '@/core/selects/selectionPopover.utils';
import ExternalMaterialListItem, {
  type IExternalMaterialListItem,
} from '@/core/supportMeasures/ExternalMaterialListItem.vue';
import { useQueryClient } from '@tanstack/vue-query';
import { immutableSort } from 'common/utils/array.utils';
import { computed, provide, readonly, ref, useTemplateRef } from 'vue';

defineOptions({ name: 'ExternalMaterialSelectionPopover' });

const MIN_SEARCH_LENGTH = 3;

type TRecommendation = Components.Schemas.RecommendedExternalMaterialDto;
type TSelectedMaterial = {
  externalMaterialId: number;
  item: IExternalMaterialListItem;
};

const emit = defineEmits<{
  'add:material': [material: TRecommendation];
  'remove:material': [id: number];
  'update:materials': [materialIds: number[]];
  hide: [];
}>();

const props = withDefaults(
  defineProps<{
    anchorElement: HTMLElement | null;
    commitOnClose?: boolean;
    mobileDrawer?: boolean;
    schoolSubjectId: number | null;
    schoolClassId: number | null;
    selectedMaterials: TSelectedMaterial[];
  }>(),
  {
    commitOnClose: false,
    mobileDrawer: false,
  },
);

const popoverRef = useTemplateRef('popoverRef');
const searchHeaderRef = useTemplateRef('searchHeaderRef');
const searchText = ref('');
const hasOpened = ref(false);
const { isMobile } = useMobile();
provide(SearchHighlightTextInjectionKey, readonly(searchText));

const trimmedSearch = computed<string>(() => searchText.value.trim());
const isSearching = computed<boolean>(() => trimmedSearch.value.length >= MIN_SEARCH_LENGTH);
const effectiveSearch = computed<string>(() => (isSearching.value ? trimmedSearch.value : ''));

const query = useQueryWithToast({
  queryKey: computed(() => [
    'supportMeasureAppRecommendations',
    props.schoolSubjectId,
    props.schoolClassId,
    effectiveSearch.value,
  ]),
  queryFn: (): Promise<TRecommendation[]> =>
    fetchExternalMaterialRecommendations({
      schoolSubjectId: props.schoolSubjectId ?? undefined,
      schoolClassId: props.schoolClassId ?? undefined,
      search: effectiveSearch.value || undefined,
    }),
  enabled: hasOpened,
});

const recommendations = computed<TRecommendation[]>(() => query.data.value ?? []);
const isFetching = computed<boolean>(() => query.isFetching.value);

// commitOnClose: toggles accumulate in this draft and emit once on close (one setMaterials call)
const draftIds = ref<number[]>([]);
const createdCustoms = ref<TRecommendation[]>([]);
// only commit if the popover was actually opened — guards against spurious hide events (e.g. initial mount)
let commitPending = false;

// resolve draft ids back to their material objects for the "selected" section (existing + recommendations + created)
const knownMaterialById = computed<Map<number, IExternalMaterialListItem>>(() => {
  const map = new Map<number, IExternalMaterialListItem>();
  for (const entry of props.selectedMaterials) map.set(entry.externalMaterialId, entry.item);
  for (const material of recommendations.value) map.set(material.id, material);
  for (const material of createdCustoms.value) map.set(material.id, material);
  return map;
});

const selectedIds = computed<Set<number>>(() =>
  props.commitOnClose ? new Set(draftIds.value) : new Set(props.selectedMaterials.map((s) => s.externalMaterialId)),
);
const isSelected = (id: number): boolean => selectedIds.value.has(id);

const selected = computed<TSelectedMaterial[]>(() => {
  if (!props.commitOnClose) return props.selectedMaterials;
  return draftIds.value
    .map((id) => {
      const item = knownMaterialById.value.get(id);
      return item ? { externalMaterialId: id, item } : null;
    })
    .filter((entry): entry is TSelectedMaterial => entry != null);
});
const recentlyUsed = computed<TRecommendation[]>(() =>
  recommendations.value
    .filter((material) => material.lastUsedAt != null && !isSelected(material.id))
    .sort((a, b) => (b.lastUsedAt ?? '').localeCompare(a.lastUsedAt ?? '')),
);
const recommended = computed<TRecommendation[]>(() =>
  recommendations.value.filter(
    (material) =>
      material.lastUsedAt == null &&
      !isSelected(material.id) &&
      (props.schoolSubjectId != null || material.matchesSchoolLevel),
  ),
);

const allApps = computed<TRecommendation[]>(() =>
  props.schoolSubjectId == null
    ? immutableSort(
        recommendations.value.filter((material) => !isSelected(material.id)),
        (a, b) => a.name.localeCompare(b.name),
      )
    : [],
);

const toggleDraft = (id: number): void => {
  draftIds.value = draftIds.value.includes(id) ? draftIds.value.filter((did) => did !== id) : [...draftIds.value, id];
};

const toggle = (material: TRecommendation): void => {
  if (props.commitOnClose) {
    toggleDraft(material.id);
    return;
  }
  if (isSelected(material.id)) emit('remove:material', material.id);
  else emit('add:material', material);
};

const removeSelected = (id: number): void => {
  if (props.commitOnClose) toggleDraft(id);
  else emit('remove:material', id);
};

const { catchError } = useToast();
const queryClient = useQueryClient();
const isCreatingCustom = ref(false);

const createCustom = (): Promise<void> =>
  catchError(async () => {
    if (isCreatingCustom.value || !trimmedSearch.value) return;
    isCreatingCustom.value = true;
    try {
      const created = await createCustomExternalMaterial({ name: trimmedSearch.value });
      await queryClient.invalidateQueries({ queryKey: ['supportMeasureAppRecommendations'] });
      if (props.commitOnClose) {
        createdCustoms.value = [...createdCustoms.value, created];
        toggleDraft(created.id);
      } else {
        emit('add:material', created);
      }
    } finally {
      isCreatingCustom.value = false;
    }
  });

const show = async (): Promise<void> => {
  searchText.value = '';
  hasOpened.value = true;
  if (props.commitOnClose) {
    draftIds.value = props.selectedMaterials.map((entry) => entry.externalMaterialId);
    createdCustoms.value = [];
    commitPending = true;
  }
  await popoverRef.value?.show();
  await focusSelectionSearchOnOpen(searchHeaderRef.value, isMobile.value);
};

const hide = (): void => popoverRef.value?.hide();

const handleHide = (): void => {
  if (props.commitOnClose && commitPending) {
    commitPending = false;
    emit('update:materials', draftIds.value);
  }
  emit('hide');
};

defineExpose({ show, hide, isOpen: computed(() => !!popoverRef.value?.isOpen) });
</script>

<template>
  <PositionablePopover
    ref="popoverRef"
    scroll-mode="content"
    :anchor-element="props.anchorElement"
    flip-strategy="both"
    :mobile-drawer-label="$t('supportMeasureEntry.materials.appChip')"
    :mobile-drawer-mode="props.mobileDrawer"
    :mobile-viewport-mode="!props.mobileDrawer"
    :offset-y="10"
    class="w-selection-popover"
    @hide="handleHide"
  >
    <template #default="{ mobileDrawer: isMobileDrawer }">
      <div class="flex min-h-0 min-w-0 w-full flex-1 flex-col" :class="!isMobileDrawer && 'max-h-selection-popover'">
        <SelectionPopoverSearchHeader
          ref="searchHeaderRef"
          v-model="searchText"
          :placeholder="$t('supportMeasureEntry.materials.searchPlaceholder')"
        />

        <div class="flex min-h-0 flex-1 flex-col gap-px overflow-y-auto overscroll-contain px-smd pb-smd">
          <template v-if="isSearching">
            <ExternalMaterialListItem
              v-for="material in recommendations"
              :key="`search-${material.id}`"
              :material="material"
              selectable
              :selected="isSelected(material.id)"
              @click="toggle(material)"
            />
            <template v-if="!isFetching && !recommendations.length">
              <p class="py-lg text-center text-secondary">{{ $t('supportMeasureEntry.materials.empty') }}</p>
              <ClickableListItem
                class="!gap-md !rounded-md !px-md !py-smd bg-tint/10"
                :disabled="isCreatingCustom"
                @click="createCustom"
              >
                <span
                  class="flex size-12 shrink-0 items-center justify-center rounded-lg border border-surface-secondary bg-white text-tint"
                >
                  <ExternalMaterialIcon class="size-icon-md" />
                </span>
                <div class="min-w-0 flex-1">
                  <span class="block truncate font-semibold text-tint">{{ trimmedSearch }}</span>
                  <span class="block truncate text-footer text-tint">
                    {{ $t('supportMeasureEntry.materials.addAsOwnApp') }}
                  </span>
                </div>
                <PlusIcon class="size-icon-md shrink-0 text-tint" />
              </ClickableListItem>
            </template>
          </template>

          <template v-else>
            <template v-if="selected.length">
              <span class="px-sm py-sm text-body font-semibold text-secondary">
                {{ $t('supportMeasureEntry.materials.selected') }}
              </span>
              <ExternalMaterialListItem
                v-for="entry in selected"
                :key="`selected-${entry.externalMaterialId}`"
                :material="entry.item"
                selectable
                :selected="true"
                @click="removeSelected(entry.externalMaterialId)"
              />
            </template>

            <template v-if="recentlyUsed.length">
              <span class="px-sm py-sm text-body font-semibold text-secondary">
                {{ $t('supportMeasureEntry.materials.recentlyUsed') }}
              </span>
              <ExternalMaterialListItem
                v-for="material in recentlyUsed"
                :key="`recent-${material.id}`"
                :material="material"
                selectable
                :selected="false"
                @click="toggle(material)"
              />
            </template>

            <template v-if="recommended.length">
              <span class="px-sm py-sm text-body font-semibold text-secondary">
                {{ $t('supportMeasureEntry.materials.recommended') }}
              </span>
              <ExternalMaterialListItem
                v-for="material in recommended"
                :key="`recommended-${material.id}`"
                :material="material"
                selectable
                :selected="false"
                @click="toggle(material)"
              />
            </template>

            <template v-if="allApps.length">
              <span class="px-sm py-sm text-body font-semibold text-secondary">
                {{ $t('supportMeasureEntry.materials.allApps') }}
              </span>
              <ExternalMaterialListItem
                v-for="material in allApps"
                :key="`all-${material.id}`"
                :material="material"
                selectable
                :selected="false"
                @click="toggle(material)"
              />
            </template>

            <EmptyPlaceholder
              v-if="!isFetching"
              :data="[...selected, ...recommendations]"
              :message="$t('supportMeasureEntry.materials.empty')"
            />
          </template>
        </div>
      </div>
    </template>
  </PositionablePopover>
</template>
